import { navigate, RouteComponentProps } from '@reach/router';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import React from 'react';
import { Helmet } from 'react-helmet';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { IOption } from 'components/Select/Select';
import {
  createProjects,
  createProjectsFn,
} from 'store/companies/companiesSlice';
import { ITasks, IUsers, RootState } from 'store/store';
import { removeDuplicateObjectsFromArray } from 'utils/happyFunctions';
import { SpaceBetween } from 'utils/style/extendable';
// import ClassicEditor from '@rastech-inc/ckeditor';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import Input from 'components/Input';
import { projectRoutesName } from 'components/MainHeader/Messages';
import notify from 'components/Notify';
import Select from 'components/Select';
import CKEditorField from 'components/CKEditorField/CKEditorField';
import Toggle from 'components/Toggle';
import ProjectTemplateView from './ProjectTemplateView';
import {
  ICompany,
  IProject,
  IProjectStore,
  IProjectTemplate,
} from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { isCompanyAdmin } from 'store/companies/companiesUtils';
import i18n, { languageDirection } from '../../../i18n';
import { getLanguageBaseOnTZ } from '../../../utils/LanguageLocalStorage';
import { black } from 'utils/style/colors';

interface IStateToProps {
  project: IProjectStore;
  company: ICompany;
  users: IUsers;
  thisUser: IThisUserState;
  allProjectTags: IOption[];
  companyTemplates: string[];
  tasks: ITasks;
}

interface IDispatchToProps {
  createProjects: (
    project: IProject,
    projectNames: string[],
    templates: IProjectTemplate[]
  ) => ReturnType<typeof createProjectsFn>;
}

interface IOwnProps {
  companyId?: string;
  projectId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  saving: boolean;
  batchAdd: boolean;
  projectNames: IOption[];
  templates: IProjectTemplate[];
}

interface IFormValues {
  name: string;
  tags: IOption[];
  description: string;
  visibleAllTasks: boolean;
}

class ProjectCreator extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  initialValues: IFormValues;

  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      saving: false,
      batchAdd: false,
      projectNames: [],
      templates: [],
    };
    this.initialValues = {
      name: props.project ? props.project.name : '',
      description: props.project ? props.project.description : '',
      visibleAllTasks: props.project ? props.project.visibleAllTasks : false,
      tags: props.project
        ? props.project.tags
          ? (props.project.tags as IProject['tags']).map((tag) => ({
              value: `${props.projectId}_${tag}`,
              label: tag,
            }))
          : []
        : [],
    };
  }

  componentDidMount() {
    if (!isCompanyAdmin(this.props.thisUser, this.props.company)) {
      navigate('/notfound');
    }
  }

  onSetProjectNames = (values: IOption[]) => {
    this.setState({
      projectNames: values,
    });
  };

  handleSave = ({ name, tags, description, visibleAllTasks }: IFormValues) => {
    const { batchAdd, projectNames, templates } = this.state;
    let finalNames: string[] = [];
    if (batchAdd) {
      finalNames = projectNames.reduce((current: string[], item: IOption) => {
        current.push(item.label.trim());
        return current;
      }, []);
    } else if (name.trim().length > 0) {
      finalNames = [name.trim()];
    }
    if (finalNames.length > 0) {
      this.setState({ saving: true });
      const companyId = Number(this.props.companyId);
      const projectId = Number(this.props.projectId);

      const tagLabels =
        tags.length > 0 ? tags.map((option) => option.label) : [];

      const project: IProject = {
        id: projectId,
        tags: tagLabels,
        name,
        companyId,
        description,
        visibleAllTasks,
        archived: false,
      };
      this.props
        .createProjects(project, finalNames, templates)
        .then(() => {
          this.setState({ saving: false });
          navigate(`/t/${companyId}/admin/project/list`);
          notify(
            i18n.t('createProject', { ns: 'companyManagement' }) as string
          );
        })
        .catch((err: any) => {
          this.setState({ saving: false });
          display(err);
        });
    } else {
      notify(
        i18n.t('pleaseEnterProjectName', { ns: 'companyManagement' }) as string,
        'warning'
      );
    }
  };

  handleCancel = () => {
    navigate(`/t/${this.props.companyId}/admin/project/list`);
  };

  private onChangeBatch = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ batchAdd: e.target.checked });

  handleSelectTemplate = (templateId: number, selected: boolean) => {
    const { templates } = this.state;
    const projectTemplate = {
      taskId: templateId,
      projectId: Number(this.props.projectId),
      allowRoot: false,
    } as IProjectTemplate;
    if (selected) {
      const newTemplates = [...templates, projectTemplate];
      this.setState({ templates: newTemplates });
    } else {
      this.setState({
        templates: templates.filter((item) => item.taskId !== templateId),
      });
    }
  };

  handleUpdateTemplate = (template: IProjectTemplate) => {
    let { templates } = this.state;
    templates = templates.filter((item) => item.taskId !== template.taskId);
    templates.push(template);
    this.setState({ templates });
    return Promise.resolve();
  };

  render() {
    const { project, allProjectTags, companyTemplates, tasks, company, users } =
      this.props;
    const { projectNames, batchAdd, templates } = this.state;
    const projectName = project && project.name;
    return (
      <React.Fragment>
        <Helmet>
          <title>
            {projectRoutesName(projectName ? 'editor' : 'create', projectName)}
          </title>
          <meta
            name="description"
            content={projectRoutesName(
              projectName ? 'editor' : 'create',
              projectName
            )}
          />
        </Helmet>
        <div className="section">
          <Toggle
            wrapperStyle={{}}
            label={i18n.t('batchAdd', { ns: 'companyManagement' }) as string}
            name="batchAdd"
            title={i18n.t('batchAdd', { ns: 'companyManagement' }) as string}
            onChange={this.onChangeBatch}
            checked={batchAdd}
          />
          <Formik
            onSubmit={this.handleSave}
            initialValues={this.initialValues}
            render={(formikBag: FormikProps<IFormValues>) => (
              // @ts-expect-error JSX error
              <Form
                onKeyDown={(event: any) => {
                  if ((event.charCode || event.keyCode) === 13) {
                    event.preventDefault();
                  }
                }}
              >
                <Field
                  type="text"
                  name="name"
                  render={(props: FieldProps) =>
                    !batchAdd ? (
                      <Input
                        {...props.field}
                        type="text"
                        id="name"
                        label={
                          i18n.t('projectName', {
                            ns: 'companyManagement',
                          }) as string
                        }
                        placeholder={
                          i18n.t('projectName', {
                            ns: 'companyManagement',
                          }) as string
                        }
                      />
                    ) : (
                      <Select
                        values={projectNames}
                        options={[]}
                        letAddChip={true}
                        placeholder={
                          i18n.t('projectNamesPlaceHolder', {
                            ns: 'companyManagement',
                          }) as string
                        }
                        multiChoice={true}
                        label={
                          i18n.t('projectNames', {
                            ns: 'companyManagement',
                          }) as string
                        }
                        onChangeValues={this.onSetProjectNames}
                      />
                    )
                  }
                />
                <Field
                  type="text"
                  name="description"
                  render={() => (
                    <CKEditorField
                      id="description"
                      label={
                        i18n.t('description', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      // editor={ClassicEditor}
                      data={formikBag.values.description}
                      onChange={(data: string) => {
                        formikBag.setFieldValue('description', data);
                      }}
                      dir={languageDirection()}
                      config={{
                        toolbar: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          '|',
                          'bulletedList',
                          'numberedList',
                          'blockQuote',
                          '|',
                          'undo',
                          'redo',
                          '|',
                          'link',
                          'insertTable',
                          '|',
                          'indent',
                          'outdent',
                        ],
                        language: getLanguageBaseOnTZ(),
                      }}
                    />
                  )}
                />
                {/*<Field
                  type="checkbox"
                  name="visibleAllTasks"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="checkbox"
                      id="visibleAllTasks"
                      label={messages.visibleAllTasks}
                      placeholder={messages.visibleAllTasks}
                      checked={formikBag.values.visibleAllTasks}
                    />
                  )}
                />*/}
                <Field
                  type="checkbox"
                  name="visibleAllTasks"
                  render={() => (
                    <Select
                      options={allProjectTags}
                      values={formikBag.values.tags}
                      onChangeValues={(tags) => {
                        formikBag.setFieldValue('tags', tags);
                      }}
                      label={
                        i18n.t('tags', { ns: 'companyManagement' }) as string
                      }
                      placeholder={
                        i18n.t('addTag', { ns: 'companyManagement' }) as string
                      }
                      letAddChip={true}
                      multiChoice={true}
                    />
                  )}
                />

                <p
                  style={{
                    textAlign: languageDirection() === 'rtl' ? 'right' : 'left',
                    color: black,
                  }}
                >
                  {
                    i18n.t('chooseTemplates', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </p>
                <div className="row">
                  {companyTemplates
                    .sort((a, b) => {
                      if (tasks.data[b].taskName < tasks.data[a].taskName) {
                        return 1;
                      }
                      return 0;
                    })
                    .filter((a) => !tasks.data[a].archived)
                    .map((templateId) => {
                      const current = templates.find(
                        (template: any) =>
                          template.taskId === Number(templateId)
                      );
                      const currentMap = current ?? undefined;
                      return (
                        <ProjectTemplateView
                          company={company}
                          users={users}
                          key={templateId}
                          template={tasks.data[templateId]}
                          onSelectTemplate={this.handleSelectTemplate}
                          onUpdateTemplate={this.handleUpdateTemplate}
                          current={currentMap}
                          autoSaveFieldChange={true}
                        />
                      );
                    })}
                </div>

                <SpaceBetween style={{ margin: '2rem 0' }}>
                  <Button
                    color="green"
                    type="submit"
                    loading={this.state.saving}
                  >
                    {i18n.t('save', { ns: 'companyManagement' }) as string}
                  </Button>
                  <Button color="red" onClick={this.handleCancel}>
                    {i18n.t('cancel', { ns: 'companyManagement' }) as string}
                  </Button>
                </SpaceBetween>
              </Form>
            )}
          />
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const companyId = Number(props.companyId);
  const projectId = Number(props.projectId);

  const company = state.companies.data[companyId];
  const allProjects = company.projects;
  const project = allProjects[projectId];
  const companyProjectsIds = Object.keys(allProjects);

  let allProjectTags: IOption[] = [];
  if (allProjects && companyProjectsIds.length > 0) {
    companyProjectsIds.forEach((pId) => {
      const projectTags = allProjects[pId]?.tags;
      if (projectTags && projectTags?.length > 0) {
        projectTags.forEach((tag: string) => {
          allProjectTags.push({ label: tag, value: `${projectId}_${tag}` });
        });
      }
    });
  }

  if (allProjectTags.length > 1) {
    allProjectTags = removeDuplicateObjectsFromArray(allProjectTags, 'label');
  }
  const companyTemplates = Object.keys(
    state.companies.data[companyId].templates
  );
  const tasks = state.tasks;
  const users = state.users;

  return {
    users,
    companyTemplates,
    tasks,
    project,
    company,
    thisUser,
    allProjectTags,
  };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => ({
  createProjects: (
    project: IProject,
    projectNames: string[],
    templates: IProjectTemplate[]
  ) =>
    dispatch(
      createProjects({
        project,
        names: projectNames,
        templates,
      })
    ).unwrap(),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectCreator);
